import { useEffect } from 'react'

import { clsx } from 'clsx'

import { runIfFn } from '../utils/index.js'
import { useMediaZoomContext } from './MediaZoomContext.js'
import { ZoomableItem } from './types.js'

interface ChildrenFnProps {
  /**
   * Function that opens the gallery at the current item's index
   */
  open: () => void
}

export type MediaZoomItemProps = {
  item: ZoomableItem
  /**
   * Render prop for exposing Gallery API
   */
  children?: (props: ChildrenFnProps) => JSX.Element

  skipRender?: boolean
  className?: string
}

export const MediaZoomItem = ({
  item,
  children,
  skipRender = false,
  className,
}: MediaZoomItemProps) => {
  const { zoomEnabled, register, unregister, openItem } = useMediaZoomContext()

  useEffect(() => {
    register(item)
    return () => unregister(item.id)
  }, [item, register, unregister])

  if (skipRender) {
    return null
  }

  if (children) {
    return runIfFn(children, { open: () => openItem(item.id) })
  }

  const contentMarkup = (
    <figure className="w-full h-full flex not-prose justify-center bg-surface-subdued">
      <img
        className="bg-surface-subdued object-cover object-center max-h-120"
        src={item.thumbnail}
        alt={item.alt}
      />
    </figure>
  )

  if (!zoomEnabled) {
    return contentMarkup
  }

  return (
    <button
      type="button"
      onClick={() => openItem(item.id)}
      className={clsx('w-full', className)}
    >
      {contentMarkup}
    </button>
  )
}
